import { Link, useOutletContext } from 'react-router-dom';
import { Card } from '../../components/primitives';
import { ParentRosterEntries } from '../../components/parent/ParentRosterEntries';
import type { LinkedChild } from '../../lib/queries/parent/useLinkedChildren';
import './ParentPages.css';

interface OutletShape {
  activeChildId: string | null;
  children: LinkedChild[];
}

/**
 * Parent home — the landing page after sign-in. Wave 3.1 ships a
 * placeholder: greeting + a card scaffold for the active child's
 * weekly summary. Wave 3.4 wires the real ratings / milestones /
 * highlights data.
 *
 * Tone is observational, not advisory — see plan §"Adapt for parent
 * audience".
 */
export function ParentHome() {
  const { activeChildId, children } = useOutletContext<OutletShape>();
  const activeChild = children.find((c) => c.id === activeChildId) ?? null;

  if (!activeChild) {
    return (
      <>
        <Card variant="opaque">
          <div className="parent-page__head">
            <span className="parent-page__eyebrow">Welcome to 4CM</span>
            <h1 className="parent-page__title">Hi there</h1>
          </div>
          <p className="parent-page__lead">
            To see your child's progress — corner ratings, goals, and coach
            summaries — you'll need to set up their account first.
          </p>
          <p className="parent-page__lead">
            <Link to="/parent/child/setup">Set up your child's account →</Link>
          </p>
        </Card>
        {/* A parent of an under-13 may have no 13+ children but still
            have linked roster entries the coach created. Show them. */}
        <ParentRosterEntries />
      </>
    );
  }

  return (
    <>
      <header className="parent-page__head">
        <span className="parent-page__eyebrow">{activeChild.teamName ?? 'Linked child'}</span>
        <h1 className="parent-page__title">{activeChild.name.split(' ')[0]}'s week</h1>
        <p className="parent-page__lead">
          A snapshot of where your child is right now across the four corners
          of the FA development model. Open their detail view for the full
          picture.
        </p>
      </header>

      <Card variant="opaque">
        <div className="parent-page__head">
          <span className="parent-page__eyebrow">Quick view</span>
          <h2 className="parent-page__title" style={{ fontSize: 22 }}>{activeChild.name}'s detail</h2>
        </div>
        <p className="parent-page__lead">
          Corner radar, active goals, and what their coach has been working
          on with them.
        </p>
        <p className="parent-page__lead">
          <Link to={`/parent/child/${activeChild.id}`}>Open {activeChild.name.split(' ')[0]}'s progress →</Link>
        </p>
      </Card>

      <Card variant="opaque">
        <div className="parent-page__placeholder">
          <span className="parent-page__placeholder-eyebrow">Coming soon</span>
          <p>
            Highlight reel. When your coach saves a clip and you've consented
            to media, it'll appear here.
          </p>
        </div>
      </Card>

      <ParentRosterEntries />
    </>
  );
}
